<template>
  <div class="login-container">
    <div class="login-header">
      <el-dropdown @command="handleLocaleChange" popper-class="floating-dropdown-popper">
        <span class="locale-trigger">
          <el-icon><ChatRound /></el-icon>
          {{ currentLocaleName }}
        </span>
        <template #dropdown>
          <el-dropdown-menu>
            <el-dropdown-item command="en">English</el-dropdown-item>
            <el-dropdown-item command="zh">中文</el-dropdown-item>
          </el-dropdown-menu>
        </template>
      </el-dropdown>
      <el-button @click="appStore.toggleTheme" circle size="small">
        <el-icon><component :is="appStore.isDark ? Sunny : Moon" /></el-icon>
      </el-button>
    </div>
    <el-card class="login-card is-never-shadow">
      <template #header>
        <div class="card-header">
          <el-icon :size="32" color="var(--el-color-primary)"><OfficeBuilding /></el-icon>
          <h2>{{ t('routes.login.welcome') }}</h2>
        </div>
      </template>
      <el-form :model="form" :rules="rules" ref="formRef" label-position="top">
        <el-form-item :label="t('routes.login.username')" prop="username">
          <el-input 
            v-model="form.username" 
            :prefix-icon="User"
            :placeholder="t('routes.login.username')"
          />
        </el-form-item>
        <el-form-item :label="t('routes.login.password')" prop="password">
          <el-input 
            v-model="form.password" 
            type="password" 
            :prefix-icon="Lock"
            :placeholder="t('routes.login.password')"
            show-password
            @keyup.enter="handleLogin"
          />
        </el-form-item>
        <el-form-item>
          <div class="remember-row">
            <el-checkbox v-model="form.rememberMe">
              {{ t('routes.login.remember-me') }}
            </el-checkbox>
            <el-button class="forgot-password-link" type="primary" link @click="openResetDialog">
              {{ t('routes.login.forgot-password') }}
            </el-button>
          </div>
        </el-form-item>
        <el-form-item>
          <StandardButton
            action="submit"
            :custom-text="t('routes.login.submit')"
            :loading="loading"
            style="width: 100%"
            @click="handleLogin"
          />
        </el-form-item>
      </el-form>
    </el-card>

    <el-dialog
      v-model="resetDialogVisible"
      :title="t('routes.reset-password.title')"
      width="640px"
      :close-on-click-modal="false"
      @close="handleResetDialogClose"
    >
      <el-steps :active="resetStep" finish-status="success" align-center class="steps-container">
        <el-step :title="t('routes.reset-password.step1-title')" />
        <el-step :title="t('routes.reset-password.step2-title')" />
        <el-step :title="t('routes.reset-password.step3-title')" />
        <el-step :title="t('routes.reset-password.step4-title')" />
      </el-steps>

      <div v-if="resetStep === 0" class="step-content">
        <el-form ref="resetIdentityFormRef" :model="resetForm" :rules="resetRules" label-position="top">
          <el-form-item :label="t('routes.reset-password.username')" prop="username">
            <el-input
              v-model="resetForm.username"
              :prefix-icon="User"
              :placeholder="t('routes.reset-password.username-placeholder')"
            />
          </el-form-item>

          <el-form-item :label="t('routes.reset-password.captcha')" prop="captcha">
            <div class="captcha-container">
              <el-input
                v-model="resetForm.captcha"
                :placeholder="t('routes.reset-password.captcha-placeholder')"
                maxlength="4"
                style="flex: 1"
              />
              <div class="captcha-image-wrapper" @click="refreshCaptcha">
                <el-icon v-if="captchaLoading" class="loading-icon is-loading"><Loading /></el-icon>
                <img v-else-if="captchaImage" :src="captchaImage" alt="captcha" class="captcha-image" />
                <span v-else class="captcha-placeholder">{{ t('common.loading') }}</span>
                <el-icon class="refresh-icon"><Refresh /></el-icon>
              </div>
            </div>
          </el-form-item>

          <div class="dialog-actions">
            <StandardButton action="cancel" size="sm" @click="resetDialogVisible = false" />
            <StandardButton
              action="submit"
              size="sm"
              :custom-text="t('routes.reset-password.send-code')"
              :loading="resetLoading"
              @click="sendResetCode"
            />
          </div>
        </el-form>
      </div>

      <div v-if="resetStep === 1" class="step-content">
        <el-form ref="resetCodeFormRef" :model="resetForm" :rules="resetRules" label-position="top">
          <el-alert :title="resetCodeSentHint" type="success" :closable="false" show-icon class="alert-message" />

          <el-form-item :label="t('routes.reset-password.verification-code')" prop="code">
            <el-input
              v-model="resetForm.code"
              :prefix-icon="Key"
              :placeholder="t('routes.reset-password.code-placeholder')"
              maxlength="6"
            />
          </el-form-item>

          <div class="resend-container">
            <span v-if="countdown > 0" class="countdown-text">
              {{ t('routes.reset-password.resend-countdown', { seconds: countdown }) }}
            </span>
            <StandardButton
              v-else
              action="retry"
              size="sm"
              display-mode="text-only"
              :custom-text="t('routes.reset-password.resend-code')"
              @click="resendResetCode"
            />
          </div>

          <div class="dialog-actions">
            <StandardButton action="prev" size="sm" :custom-text="t('routes.reset-password.previous-step')" @click="resetStep = 0" />
            <StandardButton
              action="confirm"
              size="sm"
              :custom-text="t('routes.reset-password.verify')"
              :loading="resetLoading"
              @click="verifyResetCode"
            />
          </div>
        </el-form>
      </div>

      <div v-if="resetStep === 2" class="step-content">
        <el-form ref="resetPasswordFormRef" :model="resetForm" :rules="resetRules" label-position="top">
          <el-form-item :label="t('routes.reset-password.new-password')" prop="newPassword">
            <el-input
              v-model="resetForm.newPassword"
              type="password"
              :prefix-icon="Lock"
              :placeholder="t('routes.reset-password.new-password-placeholder')"
              show-password
            />
          </el-form-item>

          <el-form-item :label="t('routes.reset-password.confirm-password')" prop="confirmPassword">
            <el-input
              v-model="resetForm.confirmPassword"
              type="password"
              :prefix-icon="Lock"
              :placeholder="t('routes.reset-password.confirm-password-placeholder')"
              show-password
            />
          </el-form-item>

          <div class="dialog-actions">
            <StandardButton action="prev" size="sm" :custom-text="t('routes.reset-password.previous-step')" @click="resetStep = 1" />
            <StandardButton
              action="submit"
              size="sm"
              :custom-text="t('routes.reset-password.submit')"
              :loading="resetLoading"
              @click="submitResetPassword"
            />
          </div>
        </el-form>
      </div>

      <div v-if="resetStep === 3" class="step-content success-content">
        <el-result
          icon="success"
          :title="t('routes.reset-password.success-message')"
          :sub-title="t('routes.reset-password.success-description')"
        >
          <template #extra>
            <StandardButton action="confirm" :custom-text="t('routes.reset-password.back-to-login')" @click="resetDialogVisible = false" />
          </template>
        </el-result>
      </div>
    </el-dialog>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onUnmounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ElMessage } from 'element-plus'
import { useAuthStore } from '../stores/auth'
import { useAppStore } from '../stores/app'
import { User, Lock, OfficeBuilding, ChatRound, Sunny, Moon, Key, Refresh, Loading } from '@element-plus/icons-vue'
import type { FormInstance, FormRules } from 'element-plus'
import StandardButton from '@/components/StandardButton.vue'
import {
  getCaptcha as getResetCaptcha,
  sendVerificationCode as sendResetCodeApi,
  verifyCode as verifyResetCodeApi,
  resetPassword as resetPasswordApi
} from '@/api/auth'

const { t } = useI18n()
const router = useRouter()
const route = useRoute()
const authStore = useAuthStore()
const appStore = useAppStore()
const formRef = ref<FormInstance>()
const loading = ref(false)
const resetDialogVisible = ref(false)
const resetStep = ref(0)
const resetLoading = ref(false)
const countdown = ref(0)
const resetIdentityFormRef = ref<FormInstance>()
const resetCodeFormRef = ref<FormInstance>()
const resetPasswordFormRef = ref<FormInstance>()
let countdownTimer: number | null = null

const form = ref({
  username: 'admin',
  password: 'password',
  rememberMe: authStore.mode === 'web' ? authStore.storageScope === 'persistent' : true
})

const rules: FormRules = {
  username: [{ required: true, message: () => t('routes.login.username'), trigger: 'blur' }],
  password: [{ required: true, message: () => t('routes.login.password'), trigger: 'blur' }]
}

const resetForm = ref({
  username: '',
  code: '',
  newPassword: '',
  confirmPassword: '',
  captcha: '',
  captchaId: ''
})

/** 校验通过后的一次性重置令牌（API v5：verify-code → reset-password 两段式） */
const resetToken = ref('')
const captchaImage = ref('')
const captchaLoading = ref(false)

const validateUsername = (_rule: unknown, value: string, callback: (error?: Error) => void) => {
  if (!value || !value.trim()) {
    callback(new Error(t('common.validation.required')))
    return
  }
  if (value.trim().length > 64) {
    callback(new Error(t('common.validation.maxLength').replace('{max}', '64')))
    return
  }
  callback()
}

const validateCode = (_rule: unknown, value: string, callback: (error?: Error) => void) => {
  if (!/^\d{6}$/.test(value || '')) {
    callback(new Error(t('routes.reset-password.invalid-code')))
    return
  }
  callback()
}

const validateConfirmPassword = (_rule: unknown, value: string, callback: (error?: Error) => void) => {
  if (!value) {
    callback(new Error(t('common.validation.required')))
    return
  }
  if (value !== resetForm.value.newPassword) {
    callback(new Error(t('routes.reset-password.password-mismatch')))
    return
  }
  callback()
}

const validateCaptcha = (_rule: unknown, value: string, callback: (error?: Error) => void) => {
  if (!value || value.length !== 4) {
    callback(new Error(t('routes.reset-password.invalid-captcha')))
    return
  }
  callback()
}

const resetRules: FormRules = {
  username: [{ validator: validateUsername, trigger: 'blur' }],
  code: [{ validator: validateCode, trigger: 'blur' }],
  captcha: [{ validator: validateCaptcha, trigger: 'blur' }],
  newPassword: [
    { required: true, message: () => t('common.validation.required'), trigger: 'blur' },
    { min: 8, message: () => t('common.validation.minLength', { min: 8 }), trigger: 'blur' }
  ],
  confirmPassword: [{ validator: validateConfirmPassword, trigger: 'blur' }]
}

const currentLocaleName = computed(() => {
  return appStore.currentLocale === 'zh' ? '中文' : 'EN'
})

const resetCodeSentHint = computed(() => {
  return `${t('routes.reset-password.code-sent')}: ${resetForm.value.username}`
})

function handleLocaleChange(lang: string) {
  appStore.setLocale(lang)
}

function openResetDialog() {
  resetDialogVisible.value = true
  resetStep.value = 0
  loadCaptcha()
}

async function loadCaptcha() {
  captchaLoading.value = true
  try {
    const captchaData = await getResetCaptcha()
    captchaImage.value = `data:image/svg+xml;base64,${captchaData.captcha_image}`
    resetForm.value.captchaId = captchaData.captcha_id
    resetForm.value.captcha = ''
  } catch (error) {
    console.error('[LoadCaptcha] Failed to load captcha:', error)
    ElMessage.error(t('common.messages.error'))
  } finally {
    captchaLoading.value = false
  }
}

async function refreshCaptcha() {
  await loadCaptcha()
}

function resetResetFormState() {
  resetStep.value = 0
  resetLoading.value = false
  countdown.value = 0
  captchaImage.value = ''
  captchaLoading.value = false
  resetToken.value = ''
  if (countdownTimer) {
    window.clearInterval(countdownTimer)
    countdownTimer = null
  }
  resetForm.value = {
    username: '',
    code: '',
    newPassword: '',
    confirmPassword: '',
    captcha: '',
    captchaId: ''
  }
  resetIdentityFormRef.value?.clearValidate()
  resetCodeFormRef.value?.clearValidate()
  resetPasswordFormRef.value?.clearValidate()
}

function handleResetDialogClose() {
  resetResetFormState()
}

function startCountdown() {
  countdown.value = 60
  if (countdownTimer) {
    window.clearInterval(countdownTimer)
  }
  countdownTimer = window.setInterval(() => {
    countdown.value--
    if (countdown.value <= 0) {
      window.clearInterval(countdownTimer!)
      countdownTimer = null
    }
  }, 1000)
}

async function sendResetCode() {
  if (!resetIdentityFormRef.value) return
  const valid = await resetIdentityFormRef.value.validate().catch(() => false)
  if (!valid) return

  resetLoading.value = true
  try {
    await sendResetCodeApi({
      username: resetForm.value.username.trim(),
      captcha: resetForm.value.captcha,
      captcha_id: resetForm.value.captchaId
    })
    ElMessage.success(t('routes.reset-password.code-sent'))
    resetStep.value = 1
    startCountdown()
  } catch (error) {
    const message = error instanceof Error ? error.message : t('common.messages.error')
    ElMessage.error(message)
    // 验证失败后刷新验证码
    await refreshCaptcha()
  } finally {
    resetLoading.value = false
  }
}

async function resendResetCode() {
  resetLoading.value = true
  try {
    // 重发窗口（验证码 TTL 内）无需图形验证码，后端直接重发
    await sendResetCodeApi({
      username: resetForm.value.username.trim(),
      captcha: '',
      captcha_id: ''
    })
    ElMessage.success(t('routes.reset-password.code-sent'))
    startCountdown()
  } catch (error) {
    const message = error instanceof Error ? error.message : t('common.messages.error')
    ElMessage.error(message)
  } finally {
    resetLoading.value = false
  }
}

async function verifyResetCode() {
  if (!resetCodeFormRef.value) return
  const valid = await resetCodeFormRef.value.validate().catch(() => false)
  if (!valid) return

  resetLoading.value = true
  try {
    const res = await verifyResetCodeApi({
      username: resetForm.value.username.trim(),
      code: resetForm.value.code
    })
    resetToken.value = res.token  // 一次性重置令牌，下一步提交
    resetStep.value = 2
  } catch (error) {
    const message = error instanceof Error ? error.message : t('routes.reset-password.code-error')
    ElMessage.error(message)
  } finally {
    resetLoading.value = false
  }
}

async function submitResetPassword() {
  if (!resetPasswordFormRef.value) return
  const valid = await resetPasswordFormRef.value.validate().catch(() => false)
  if (!valid) return

  resetLoading.value = true
  try {
    if (!resetToken.value) {
      throw new Error(t('routes.reset-password.code-error'))
    }
    await resetPasswordApi({
      token: resetToken.value,
      new_password: resetForm.value.newPassword
    })
    resetStep.value = 3
  } catch (error) {
    const message = error instanceof Error ? error.message : t('common.messages.error')
    ElMessage.error(message)
  } finally {
    resetLoading.value = false
  }
}

async function handleLogin() {
  if (!formRef.value) return
  await formRef.value.validate((valid) => {
    if (valid) {
      loading.value = true
      authStore
        .login(form.value.username, form.value.password, {
          rememberMe: form.value.rememberMe
        })
        .then(() => {
          const redirect = typeof route.query.redirect === 'string' ? route.query.redirect : '/dashboard'
          router.push(redirect)
        })
        .catch((error) => {
          const message = error instanceof Error ? error.message : t('common.messages.error')
          ElMessage.error(message)
        })
        .finally(() => {
          loading.value = false
        })
    }
  })
}

onUnmounted(() => {
  if (countdownTimer) {
    window.clearInterval(countdownTimer)
    countdownTimer = null
  }
})
</script>

<style scoped>
.login-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background: linear-gradient(135deg, var(--el-color-primary-light-9) 0%, var(--el-bg-color) 100%);
  padding: 20px;
}
.login-header {
  position: fixed;
  top: 20px;
  right: 20px;
  display: flex;
  gap: 12px;
  align-items: center;
}
.locale-trigger {
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--el-text-color-regular);
  font-size: 14px;
}
.login-card {
  width: 100%;
  max-width: 400px;
}
.card-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
}
.card-header h2 {
  margin: 0;
  font-size: 20px;
  color: var(--el-text-color-primary);
}

.remember-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
}

.forgot-password-link {
  margin-left: auto;
}

.steps-container {
  margin-bottom: 24px;
}

.step-content {
  min-height: 220px;
}

.dialog-actions {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.alert-message {
  margin-bottom: 16px;
}

.resend-container {
  margin-top: -8px;
  margin-bottom: 16px;
  text-align: right;
}

.countdown-text {
  color: var(--el-text-color-secondary);
  font-size: 14px;
}

.success-content {
  min-height: auto;
}

.captcha-container {
  display: flex;
  gap: 12px;
  align-items: center;
}

.captcha-image-wrapper {
  width: 120px;
  height: 40px;
  border: 1px solid var(--el-border-color);
  border-radius: 4px;
  overflow: hidden;
  cursor: pointer;
  position: relative;
  transition: all 0.3s;
  display: flex;
  justify-content: center;
  align-items: center;
  background-color: var(--el-fill-color-light);
}

.captcha-image-wrapper:hover {
  border-color: var(--el-color-primary);
}

.captcha-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.refresh-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  font-size: 20px;
  color: var(--el-color-primary);
  opacity: 0;
  transition: opacity 0.3s;
}

.captcha-image-wrapper:hover .refresh-icon {
  opacity: 0.8;
}

.loading-icon {
  font-size: 24px;
  color: var(--el-color-primary);
}

.captcha-placeholder {
  font-size: 12px;
  color: var(--el-text-color-placeholder);
}
</style>
